import axios from 'axios'

// Enhanced error types for better error handling
export const EMAIL_ERROR_TYPES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  ATTACHMENT_ERROR: 'ATTACHMENT_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  RETRY_EXHAUSTED: 'RETRY_EXHAUSTED'
}

const blobToBase64 = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(blob)
    reader.onloadend = () => {
      const base64data = reader.result
      resolve(base64data.split(',')[1])
    }
    reader.onerror = error => {
      reject(error)
    }
  })

const prepareAttachments = async files => {
  try {
    console.log(`Preparing ${files.length} attachments for email`)

    // Validate attachment count
    if (files.length > 10) {
      throw new Error('Maximum 10 attachments allowed per email')
    }

    const attachments = await Promise.all(
      files.map(async (file, index) => {
        try {
          const content = await blobToBase64(file)

          // Check individual file size (5MB limit per file)
          const sizeInMB = (content.length * 0.75) / (1024 * 1024) // Base64 is ~33% larger
          if (sizeInMB > 5) {
            throw new Error(`Attachment ${index + 1} exceeds 5MB limit`)
          }

          return {
            filename: file.filename || `Dokument_${index + 1}.pdf`,
            content,
            type: file.type || 'application/pdf',
            disposition: 'attachment',
          }
        } catch (error) {
          console.error(`Error processing attachment ${index + 1}:`, error)
          throw new Error(`Failed to process attachment ${index + 1}: ${error.message}`)
        }
      })
    )

    return attachments
  } catch (error) {
    console.error('Error preparing attachments:', error)
    const attachmentError = new Error(error.message)
    attachmentError.type = EMAIL_ERROR_TYPES.ATTACHMENT_ERROR
    attachmentError.originalError = error
    throw attachmentError
  }
}

// Retry utility with exponential backoff
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  // eslint-disable-next-line consistent-return
  async function attemptRequest(attempt) {
    try {
      console.log(`Email sending attempt ${attempt}/${maxRetries}`)
      return await fn()
    } catch (error) {
      if (attempt === maxRetries) {
        console.error(`All ${maxRetries} email sending attempts failed`)
        const retryError = new Error(`Email sending failed after ${maxRetries} attempts`)
        retryError.type = EMAIL_ERROR_TYPES.RETRY_EXHAUSTED
        retryError.originalError = error
        retryError.attempt = attempt
        throw retryError
      }

      // Don't retry on validation errors (4xx status codes)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        console.log('Skipping retry for validation error')
        throw error
      }

      const delay = baseDelay * (2 ** (attempt - 1))
      console.log(`Attempt ${attempt} failed, retrying in ${delay}ms...`)

      await new Promise(resolve => setTimeout(resolve, delay))
      return attemptRequest(attempt + 1)
    }
  }

  return attemptRequest(1)
}

const sendEmail = async (email, options = {}) => {
  const startTime = Date.now()
  const {
    maxRetries = 3,
    timeout = 30000, // 30 seconds
    enableRetry = true
  } = options

  console.log(`Starting email send to: ${email.to}`)

  // Input validation
  if (!email.to || !email.subject) {
    const error = {
      type: EMAIL_ERROR_TYPES.VALIDATION_ERROR,
      message: 'Email address and subject are required',
      details: { to: email.to, subject: email.subject }
    }
    console.error('Email validation failed:', error)
    return { success: false, error }
  }

  try {
    const emailData = {
      to: email.to,
      subject: email.subject,
      text: email.text,
      template_id: email.template_id,
      dynamic_template_data: email.dynamic_template_data,
    }

    // Prepare attachments if any
    if (email.attachments && email.attachments.length > 0) {
      console.log(`Preparing ${email.attachments.length} attachments`)
      emailData.attachments = await prepareAttachments(email.attachments)
    }

    // Create axios config with timeout
    const axiosConfig = {
      timeout,
      headers: {
        'Content-Type': 'application/json'
      }
    }

    // Use environment-specific email service URL
    const emailServiceUrl = process.env.VUE_APP_EMAIL_SERVICE_URL || 'https://sendmail.der-lagermeister.de'

    // Define the email sending function
    const sendEmailRequest = async () => axios.post(
        `${emailServiceUrl}/send-email`,
        emailData,
        axiosConfig
      )

    // Send email with or without retry
    const response = enableRetry
      ? await retryWithBackoff(sendEmailRequest, maxRetries, 1000)
      : await sendEmailRequest()

    const duration = Date.now() - startTime
    console.log(`Email sent successfully in ${duration}ms`)

    // Handle new JSON response format from enhanced SendGrid service
    const responseData = response.data

    return {
      success: true,
      message: responseData.message || 'Email sent successfully',
      data: {
        requestId: responseData.requestId,
        messageId: responseData.messageId,
        to: responseData.to,
        duration: responseData.duration || duration,
        timestamp: responseData.timestamp
      }
    }
  } catch (error) {
    const duration = Date.now() - startTime
    console.error(`Email sending failed after ${duration}ms:`, error)

    let emailError = {
      type: EMAIL_ERROR_TYPES.SERVER_ERROR,
      message: 'Unknown error occurred',
      duration
    }

    // Handle different error types
    if (error.type) {
      // Custom error from our utilities
      emailError = { ...error, duration }
    } else if (error.code === 'ECONNABORTED') {
      emailError = {
        type: EMAIL_ERROR_TYPES.TIMEOUT_ERROR,
        message: `Request timeout after ${timeout}ms`,
        duration
      }
    } else if (error.response) {
      // Server responded with error
      const {status} = error.response
      const responseData = error.response.data

      if (status >= 400 && status < 500) {
        emailError = {
          type: EMAIL_ERROR_TYPES.VALIDATION_ERROR,
          message: responseData.message || 'Validation error',
          details: responseData.errors || [],
          errorCode: responseData.errorCode,
          duration
        }
      } else if (status >= 500) {
        emailError = {
          type: EMAIL_ERROR_TYPES.SERVER_ERROR,
          message: responseData.message || 'Server error',
          errorCode: responseData.errorCode,
          duration
        }
      }
    } else if (error.request) {
      // Network error
      emailError = {
        type: EMAIL_ERROR_TYPES.NETWORK_ERROR,
        message: 'Network error - could not reach email service',
        duration
      }
    }

    return {
      success: false,
      error: emailError
    }
  }
}

// Enhanced email service with additional utilities
export const emailService = {
  send: sendEmail,

  // Check if email service is healthy
  async checkHealth() {
    try {
      const emailServiceUrl = process.env.VUE_APP_EMAIL_SERVICE_URL || 'https://sendmail.der-lagermeister.de'
      console.log('🔍 Email service URL from env:', emailServiceUrl)
      console.log('🔍 All env vars:', {
        VUE_APP_EMAIL_SERVICE_URL: process.env.VUE_APP_EMAIL_SERVICE_URL,
        VUE_APP_DATABASE_HOST: process.env.VUE_APP_DATABASE_HOST,
        NODE_ENV: process.env.NODE_ENV
      })
      const response = await axios.get(`${emailServiceUrl}/health`, {
        timeout: 5000
      })
      return {
        healthy: true,
        data: response.data
      }
    } catch (error) {
      console.error('🚨 Email service health check error:', error.message)
      return {
        healthy: false,
        error: error.message
      }
    }
  },

  // Get email status (placeholder for future implementation)
  async getEmailStatus(requestId) {
    try {
      const emailServiceUrl = process.env.VUE_APP_EMAIL_SERVICE_URL || 'https://sendmail.der-lagermeister.de'
      const response = await axios.get(
        `${emailServiceUrl}/email-status/${requestId}`,
        { timeout: 5000 }
      )
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.message
      }
    }
  }
}

export default sendEmail
